import React, { useMemo } from "react";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useDeleteAccountGroupsBulkMutation } from "@api/account-group.api";
import { businessReachClause, useBusinessNamesFor } from "../reachCopy";

interface GroupConstituent {
    id: string;
    entityId: number;
}

interface Props {
    show: boolean;
    onHide: () => void;
    name: string;
    groups: GroupConstituent[];
}

export const DeleteGroupModal: React.FC<Props> = ({
    show,
    onHide,
    name,
    groups,
}) => {
    const deleteMutation = useDeleteAccountGroupsBulkMutation();
    const { toast } = useToaster();
    const businessNamesFor = useBusinessNamesFor();

    const uniqueEntityIds = useMemo(
        () => Array.from(new Set(groups.map((group) => group.entityId))),
        [groups],
    );
    const reach = businessReachClause(businessNamesFor(uniqueEntityIds));

    const handleConfirm = async () => {
        try {
            const results = await deleteMutation.mutateAsync(
                groups.map(({ id, entityId }) => ({ groupId: id, entityId })),
            );
            const didEveryDeleteSucceed =
                results.length === groups.length &&
                results.every((result) => result.success);
            if (!didEveryDeleteSucceed) {
                return;
            }
            toast(`Deleted group "${name}"`);
            onHide();
        } catch {
            // error toast handled by axios interceptor
        }
    };

    return (
        <ConfirmationModal
            title={`Delete "${name}"?`}
            question={
                <p>
                    This removes only the group. Its accounts and subgroups move
                    up one level. This reaches {reach}. This action cannot be
                    undone.
                </p>
            }
            yes="Delete"
            no="Cancel"
            onConfirm={handleConfirm}
            onReject={onHide}
            show={show}
            onHide={onHide}
        />
    );
};
